import { CrawledPagesLayout } from './crawled-pages-layout';
import { RulesResultsLayout } from './rules-results-layout';
import { StatusController } from './status-controller';
import { IframeController } from './iframe-controller';
import { RulesViewHighlightController } from './rules-view-highlight-controller';
import { RuleDetailsPopupController } from './rule-details-popup-controller';

function isSubmitSampleForceEnabled() {
	const forceFlag = window?.a11yfixData?.forceShowSubmitSample;

	if (typeof forceFlag === 'boolean') {
		return forceFlag;
	}

	if (typeof forceFlag === 'number') {
		return forceFlag === 1;
	}

	if (typeof forceFlag === 'string') {
		const normalized = forceFlag.trim().toLowerCase();
		return normalized === '1' || normalized === 'true' || normalized === 'yes' || normalized === 'on';
	}

	return false;
}

export class CrawlerLayout {
	constructor(i18n) {
		this.__ = i18n;
		this.root = document.getElementById('a11yfix-admin-app');
		this.tabButtons = Array.from(document.querySelectorAll('.a11yfix-tab[data-tab-target]'));
		this.tabPanels = Array.from(document.querySelectorAll('.a11yfix-tab-panel'));
		this.iframeTabButton = document.getElementById('a11yfix-tab-iframe');
		this.rulesViewTabButton = document.getElementById('a11yfix-tab-rules-view');
		this.startButton = document.getElementById('a11yfix-start');
		this.runIframeButton = document.getElementById('a11yfix-run-iframe');
		this.runIframeAllButton = document.getElementById('a11yfix-run-iframe-all');
		this.runIframeCurrentButton = document.getElementById('a11yfix-run-iframe-current');
		this.stopTestButton = document.getElementById('a11yfix-stop-test');
		this.clearButton = document.getElementById('a11yfix-clear');
		this.clearRulesButton = document.getElementById('a11yfix-clear-rules');
		this.resultsScopeToggleButton = document.getElementById('a11yfix-results-scope-toggle');
		this.rulesRetestPageButton = document.getElementById('a11yfix-rules-retest-page');
		this.rulesModeSwitchButton = document.getElementById('a11yfix-rules-mode-switch');
		this.viewSampleButton = document.getElementById('a11yfix-view-sample');
		this.submitSampleButton = document.getElementById('a11yfix-submit-sample');
		this.submitSampleHintNode = document.getElementById('a11yfix-submit-sample-hint');
		this.ruleToggleButtons = Array.from(document.querySelectorAll('[data-rule-toggle]'));
		this.depthInput = document.getElementById('a11yfix-depth');
		this.statusNode = document.getElementById('a11yfix-status');
		this.previewIframe = document.getElementById('a11yfix-preview-iframe');
		this.rulesPreviewIframe = document.getElementById('a11yfix-rules-iframe');
		this.crawledResultsNode = document.getElementById('a11yfix-crawled-results');
		this.rulesResultsNode = document.getElementById('a11yfix-rule-results');
		this.crawledPagesLayout = new CrawledPagesLayout(this.__, this.crawledResultsNode);
		this.rulesResultsLayout = new RulesResultsLayout(this.__, this.rulesResultsNode);
		this.statusController = new StatusController(this.statusNode);
		this.iframeController = new IframeController(this.previewIframe, this.rulesPreviewIframe);
		this.rulesViewHighlightController = new RulesViewHighlightController(
			this.ruleToggleButtons,
			() => this.rulesPreviewIframe,
			this.__
		);
		this.popupController = new RuleDetailsPopupController(this.__, this.root);
		this.isTesting = false;
		this.testTabActionsEnabled = true;
		this.resizeSyncTimeoutId = 0;
		this.onWindowResize = () => {
			window.clearTimeout(this.resizeSyncTimeoutId);
			this.resizeSyncTimeoutId = window.setTimeout(() => {
				this.syncPreviewIframeHeight();
				this.syncRulesViewIframeHeight();
			}, 80);
		};
	}

	isReady() {
		return Boolean(
			this.root &&
			this.tabButtons.length > 0 &&
			this.startButton &&
			this.runIframeButton &&
			this.runIframeAllButton &&
			this.runIframeCurrentButton &&
			this.stopTestButton &&
			this.clearButton &&
			this.clearRulesButton &&
			this.rulesModeSwitchButton &&
			this.ruleToggleButtons.length > 0 &&
			this.depthInput &&
			this.statusNode &&
			this.previewIframe &&
			this.rulesPreviewIframe &&
			this.crawledPagesLayout.isReady() &&
			this.rulesResultsLayout.isReady()
		);
	}

	bindEvents({ onStart, onRunIframe, onRunIframeAll, onRunIframeCurrent, onStopTest, onClearCrawled, onClearRules, onToggleRuleHighlight, onRuleDetails, onRuleUrlOpen, onCrawledPageUrlOpen, onRulesViewModeSwitch, onRetestRulesViewPage, onViewSample, onSubmitSample }) {
		this.bindTabEvents();
		this.startButton.addEventListener('click', onStart);
		this.runIframeButton.addEventListener('click', onRunIframe);
		this.runIframeAllButton.addEventListener('click', () => {
			if (typeof onRunIframeAll === 'function') {
				onRunIframeAll();
			}
		});
		this.runIframeCurrentButton.addEventListener('click', () => {
			if (typeof onRunIframeCurrent === 'function') {
				onRunIframeCurrent();
			}
		});
		this.stopTestButton.addEventListener('click', onStopTest);
		this.clearButton.addEventListener('click', onClearCrawled);
		this.clearRulesButton.addEventListener('click', onClearRules);
		if (this.resultsScopeToggleButton) {
			this.resultsScopeToggleButton.addEventListener('click', () => {
				this.toggleResultsScopeMode();
			});
		}
		this.rulesModeSwitchButton.addEventListener('click', () => {
			if (typeof onRulesViewModeSwitch === 'function') {
				onRulesViewModeSwitch();
			}
		});

		if (this.rulesRetestPageButton) {
			this.rulesRetestPageButton.addEventListener('click', () => {
				if (typeof onRetestRulesViewPage === 'function') {
					onRetestRulesViewPage();
				}
			});
		}

		if (this.submitSampleButton) {
			this.submitSampleButton.addEventListener('click', () => {
				if (typeof onSubmitSample === 'function') {
					onSubmitSample();
				}
			});
		}

		if (this.viewSampleButton) {
			this.viewSampleButton.addEventListener('click', () => {
				if (typeof onViewSample === 'function') {
					onViewSample();
				}
			});
		}

		for (const ruleToggleButton of this.ruleToggleButtons) {
			ruleToggleButton.addEventListener('click', () => {
				const ruleKey = ruleToggleButton.dataset.ruleToggle || '';

				if (!ruleKey) {
					return;
				}

				onToggleRuleHighlight(ruleKey);
			});
		}

		this.rulesResultsLayout.bindRuleDetails(onRuleDetails);
		this.rulesResultsLayout.bindRuleUrlOpen(onRuleUrlOpen);
		this.crawledPagesLayout.bindPageUrlOpen(onCrawledPageUrlOpen);
		this.rulesViewHighlightController.reset();
		this.setTestTabActionsEnabled(true);
		this.setTestingState(false);
		this.setRulesViewModeSwitchState('live', false);
		this.setSubmitSampleButtonState(false, {
			hint: ''
		});
		this.refreshIframeTabsAvailability();
		this.popupController.ensurePopup();
		this.updateResultsScopeToggleLabel();
	}

	updateResultsScopeToggleLabel() {
		if (!this.resultsScopeToggleButton) {
			return;
		}

		const scopeMode = this.rulesResultsLayout.getScopeMode();
		this.resultsScopeToggleButton.textContent = scopeMode === 'all'
			? this.__( 'Scope: page only', 'a11yfix' )
			: this.__( 'Scope: all', 'a11yfix' );
	}

	toggleResultsScopeMode() {
		this.rulesResultsLayout.toggleScopeMode();
		this.updateResultsScopeToggleLabel();
	}

	setSubmitSampleButtonState(isVisible, options = {}) {
		if (!this.submitSampleButton) {
			return;
		}

		const isForceMode = isSubmitSampleForceEnabled();

		if (isForceMode) {
			const disabled = options?.disabled === true;

			if (this.viewSampleButton) {
				this.viewSampleButton.style.display = '';
				this.viewSampleButton.disabled = false;
				this.viewSampleButton.setAttribute('aria-hidden', 'false');
			}

			this.submitSampleButton.style.display = '';
			this.submitSampleButton.disabled = disabled;
			this.submitSampleButton.setAttribute('aria-hidden', 'false');
			this.submitSampleButton.title = typeof options?.title === 'string' && options.title.length > 0
				? options.title
				: this.__( 'Test mode: submit sample button is force-enabled.', 'a11yfix' );

			if (this.submitSampleHintNode) {
				const forcedHint = typeof options?.hint === 'string' && options.hint.length > 0
					? options.hint
					: this.__( 'We detected some blocks displacement, can you send us anonymous data', 'a11yfix' );
				this.submitSampleHintNode.textContent = forcedHint;
				this.submitSampleHintNode.style.display = '';
			}

			return;
		}

		const visible = Boolean(isVisible);
		const disabled = options?.disabled === true;
		const titleText = typeof options?.title === 'string' ? options.title : '';
		const hintText = typeof options?.hint === 'string' ? options.hint : '';

		if (this.viewSampleButton) {
			this.viewSampleButton.style.display = visible ? '' : 'none';
			this.viewSampleButton.disabled = !visible;
			this.viewSampleButton.setAttribute('aria-hidden', visible ? 'false' : 'true');
		}

		this.submitSampleButton.style.display = visible ? '' : 'none';
		this.submitSampleButton.disabled = !visible || disabled;
		this.submitSampleButton.setAttribute('aria-hidden', visible ? 'false' : 'true');

		if (titleText) {
			this.submitSampleButton.title = titleText;
		} else {
			this.submitSampleButton.removeAttribute('title');
		}

		if (this.submitSampleHintNode) {
			this.submitSampleHintNode.textContent = hintText;
			this.submitSampleHintNode.style.display = hintText.length > 0 ? '' : 'none';
		}
	}

	setTestTabActionsEnabled(isEnabled) {
		this.testTabActionsEnabled = Boolean(isEnabled);
		this.runIframeAllButton.disabled = this.isTesting || !this.testTabActionsEnabled;
		this.runIframeCurrentButton.disabled = this.isTesting || !this.testTabActionsEnabled;
		this.stopTestButton.disabled = !this.isTesting || !this.testTabActionsEnabled;
	}

	isIframeEmpty(iframeNode) {
		if (!iframeNode) {
			return true;
		}

		const sourceUrl = String(iframeNode.getAttribute('src') || iframeNode.src || '').trim();

		if (!sourceUrl || sourceUrl === 'about:blank') {
			return true;
		}

		try {
			const parsed = new URL(sourceUrl, window.location.href);
			return parsed.href === 'about:blank';
		} catch {
			return false;
		}
	}

	setTabEnabled(tabButton, isEnabled) {
		if (!tabButton) {
			return;
		}

		tabButton.disabled = !isEnabled;
		tabButton.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
	}

	refreshIframeTabsAvailability() {
		this.setTabEnabled(this.iframeTabButton, !this.isIframeEmpty(this.previewIframe));
		this.setTabEnabled(this.rulesViewTabButton, !this.isIframeEmpty(this.rulesPreviewIframe));
	}

	getDepthLimit() {
		const parsedDepth = Number.parseInt(this.depthInput.value, 10);

		if (Number.isNaN(parsedDepth) || parsedDepth < 0) {
			return 0;
		}

		if (parsedDepth > 6) {
			return 6;
		}

		return parsedDepth;
	}

	setBusyState(isBusy) {
		this.startButton.disabled = isBusy;
		this.clearButton.disabled = isBusy;
		this.clearRulesButton.disabled = isBusy;
		this.depthInput.disabled = isBusy;
		this.runIframeButton.disabled = isBusy || this.isTesting;
		this.runIframeAllButton.disabled = isBusy || this.isTesting || !this.testTabActionsEnabled;
		this.runIframeCurrentButton.disabled = isBusy || this.isTesting || !this.testTabActionsEnabled;
	}

	setTestingState(isRunningTest) {
		this.isTesting = isRunningTest;
		this.runIframeButton.disabled = isRunningTest;
		this.runIframeAllButton.disabled = isRunningTest || !this.testTabActionsEnabled;
		this.runIframeCurrentButton.disabled = isRunningTest || !this.testTabActionsEnabled;
		this.stopTestButton.disabled = !isRunningTest || !this.testTabActionsEnabled;
	}

	activateTab(tabTargetId) {
		for (const tabButton of this.tabButtons) {
			const isActive = tabButton.dataset.tabTarget === tabTargetId;
			tabButton.classList.toggle('is-active', isActive);
			tabButton.setAttribute('aria-selected', isActive ? 'true' : 'false');
		}

		for (const panelNode of this.tabPanels) {
			const isActive = panelNode.id === tabTargetId;
			panelNode.classList.toggle('is-active', isActive);
			panelNode.hidden = !isActive;
		}

		if (tabTargetId === 'a11yfix-panel-rules-view') {
			window.requestAnimationFrame(() => {
				this.syncRulesViewIframeHeight();
			});

			window.setTimeout(() => {
				this.syncRulesViewIframeHeight();
			}, 50);
		}

		if (tabTargetId === 'a11yfix-panel-iframe') {
			window.requestAnimationFrame(() => {
				this.syncPreviewIframeHeight();
			});

			window.setTimeout(() => {
				this.syncPreviewIframeHeight();
			}, 50);
		}
	}

	showIframeTab() {
		this.activateTab('a11yfix-panel-iframe');
	}

	showRulesViewTab() {
		this.activateTab('a11yfix-panel-rules-view');
	}

	bindTabEvents() {
		window.addEventListener('resize', this.onWindowResize);

		for (const tabButton of this.tabButtons) {
			tabButton.addEventListener('click', () => {
				const tabTargetId = tabButton.dataset.tabTarget;

				if (!tabTargetId) {
					return;
				}

				this.activateTab(tabTargetId);
			});
		}
	}

	setStatus(message) {
		this.statusController.setStatus(message);
	}

	setStatusError(message) {
		this.statusController.setStatusError(message);
	}

	isStatusErrorActive() {
		return this.statusController.isStatusErrorActive();
	}

	renderResults(crawledPages, pagesWithRules) {
		this.renderCrawledPages(crawledPages);
		this.renderPagesWithRules(pagesWithRules);
	}

	renderCrawledPages(pages) {
		this.crawledPagesLayout.render(pages);
	}

	setCommonLayoutDetection(layoutDetection) {
		this.crawledPagesLayout.setLayoutDetection(layoutDetection);
	}

	renderPagesWithRules(pages) {
		this.rulesResultsLayout.render(pages);
	}

	updateRuleStatus(pageIndex, page) {
		this.rulesResultsLayout.updateRuleStatus(pageIndex, page);
	}

	clearIframe() {
		this.iframeController.clearPreviewIframe();
		this.refreshIframeTabsAvailability();
	}

	clearRulesViewIframe() {
		this.iframeController.clearRulesViewIframe();
		this.rulesViewHighlightController.reset();
		this.setRulesViewModeSwitchState('live', false);
		this.refreshIframeTabsAvailability();
	}

	setRulesViewModeSwitchState(mode, isEditorViewAvailable = false) {
		if (!this.rulesModeSwitchButton) {
			return;
		}

		if (mode === 'block-editor') {
			this.rulesModeSwitchButton.textContent = this.__( 'View live', 'a11yfix' );
			this.rulesModeSwitchButton.disabled = false;
			return;
		}

		this.rulesModeSwitchButton.textContent = this.__( 'View in WP editor', 'a11yfix' );
		this.rulesModeSwitchButton.disabled = !isEditorViewAvailable;
	}

	loadIframeUrl(url) {
		if (typeof url === 'string' && url.trim().length > 0 && url.trim() !== 'about:blank') {
			this.setTabEnabled(this.iframeTabButton, true);
		}

		return this.iframeController.loadIframeUrl(url);
	}

	loadRulesViewUrl(url) {
		if (typeof url === 'string' && url.trim().length > 0 && url.trim() !== 'about:blank') {
			this.setTabEnabled(this.rulesViewTabButton, true);
		}

		return this.iframeController.loadRulesViewUrl(url);
	}

	syncRulesViewIframeHeight() {
		this.iframeController.syncRulesViewIframeHeight();
	}

	syncPreviewIframeHeight() {
		this.iframeController.syncPreviewIframeHeight();
	}

	setRulesViewSelectorsByRule(failedSelectorsByRule, page = null) {
		this.rulesViewHighlightController.setRulesViewSelectorsByRule(failedSelectorsByRule, page);
	}

	setRulesToggleButtonsState(selectorsByRule, activeRuleKeys) {
		this.rulesViewHighlightController.setRulesToggleButtonsState(selectorsByRule, activeRuleKeys);
	}

	restoreRulesViewToggleButtonsState() {
		this.rulesViewHighlightController.restoreRulesViewToggleButtonsState();
	}

	disableRulesViewToggleButtons() {
		this.rulesViewHighlightController.disableRulesViewToggleButtons();
	}

	bindRulesViewIframeNavigation(onNavigate) {
		this.iframeController.bindRulesViewIframeNavigation(onNavigate);
	}

	bindPreviewIframeNavigation(onNavigate) {
		this.iframeController.bindPreviewIframeNavigation(onNavigate);
	}

	getRulesViewIframeNavigationData() {
		return this.iframeController.getRulesViewIframeNavigationData();
	}

	getPreviewIframeNavigationData() {
		return this.iframeController.getPreviewIframeNavigationData();
	}

	applyRulesHighlightsFromActiveRules(shouldScroll = true) {
		return this.rulesViewHighlightController.applyRulesHighlightsFromActiveRules(shouldScroll);
	}

	buildRulesViewWpBlocksMap() {
		return this.rulesViewHighlightController.buildRulesViewWpBlocksMap();
	}

	recordRulesViewMapSnapshot(snapshot) {
		this.rulesViewHighlightController.recordRulesViewMapSnapshot(snapshot);
	}

	mapLiveBlocksToEditorAndHighlight(liveMap) {
		return this.rulesViewHighlightController.mapLiveBlocksToEditorAndHighlight(liveMap);
	}

	clearRulesViewHighlights() {
		return this.rulesViewHighlightController.clearRulesViewHighlights();
	}

	toggleRulesViewRule(ruleKey) {
		return this.rulesViewHighlightController.toggleRulesViewRule(ruleKey);
	}

	showImageAltPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'imageAlt');
	}

	showLinksPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'linkTitle');
	}

	showHeadingPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'headingOrder');
	}

	showFormLabelsPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'formLabels');
	}

	showAccessibleNamePreferredSourcePopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'accessibleNamePreferredSource');
	}

	showAriaLabelledbyPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'ariaLabelledbyIntegrity');
	}

	showAriaHiddenInteractivePopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'ariaHiddenInteractive');
	}

	showKeyboardFocusablePopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'keyboardFocusable');
	}

	showLowContrastPopup(page) {
		this.popupController.showRuleDetailsPopup(page, 'lowColorContrast');
	}

	showSubmitSamplePopup(samplePayload) {
		this.popupController.showSubmitSamplePayloadPopup(samplePayload);
	}
}
